import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FileText, Book, CheckSquare, Info, ChevronLeft, Square, Check, Share2 } from 'lucide-react';
import { useAuth } from '@clerk/clerk-react';
import { useAuthenticatedFetch } from '../utils/api';
import { toast } from 'react-hot-toast';
import Loader from './Loader';
import { useTheme } from '../contexts/ThemeContext';

const SharedClassView = () => {
  const { shareToken } = useParams();
  const navigate = useNavigate();
  const { isLoaded, isSignedIn } = useAuth();
  const authenticatedFetch = useAuthenticatedFetch();
  const { theme } = useTheme();
  
  const [classData, setClassData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAdding, setIsAdding] = useState(false);
  const [selectedLectures, setSelectedLectures] = useState(new Set());
  const [selectAll, setSelectAll] = useState(true);

  useEffect(() => {
    let isMounted = true;

    const fetchClass = async () => {
      if (!isLoaded || !isSignedIn) return;
      
      try {
        const response = await authenticatedFetch(
          `https://classify-alpha-a1e5331e0be0.herokuapp.com/api/classes/share/${shareToken}/view`
        );
        
        if (!response.ok) {
          throw new Error('Failed to fetch class');
        }

        const data = await response.json();
        if (isMounted) {
          setClassData(data);
          // By default, select all lectures
          setSelectedLectures(new Set(data.lectures.map(lecture => lecture.id)));
        }
      } catch (error) {
        if (isMounted) {
          console.error('Error fetching class:', error);
          setError('Failed to load class');
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    if (isLoaded && isSignedIn) {
      fetchClass();
    }

    return () => {
      isMounted = false;
    };
  }, [shareToken, isLoaded, isSignedIn]);

  const handleLectureToggle = (lectureId) => {
    const newSelected = new Set(selectedLectures);
    if (newSelected.has(lectureId)) {
      newSelected.delete(lectureId);
      setSelectAll(false);
    } else {
      newSelected.add(lectureId);
      if (newSelected.size === classData.lectures.length) {
        setSelectAll(true);
      }
    }
    setSelectedLectures(newSelected);
  };

  const handleSelectAllToggle = () => {
    if (selectAll) {
      setSelectedLectures(new Set());
    } else {
      setSelectedLectures(new Set(classData.lectures.map(lecture => lecture.id)));
    }
    setSelectAll(!selectAll);
  };

  const handleAddToAccount = async () => {
    if (!shareToken) return;
    
    setIsAdding(true);
    try {
      const response = await authenticatedFetch(
        `https://classify-alpha-a1e5331e0be0.herokuapp.com/api/classes/${shareToken}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            selectedLectures: Array.from(selectedLectures)
          })
        }
      );

      if (response.ok) {
        const data = await response.json();
        toast.success('Class added to your account');
        navigate(`/class/${data.id}/lectures`);
      } else {
        const error = await response.json();
        toast.error(error.message || 'Failed to add class');
      }
    } catch (error) {
      console.error('Error adding class:', error);
      toast.error('Failed to add class');
    } finally {
      setIsAdding(false);
    }
  };

  if (!isLoaded || !isSignedIn) {
    return (
      <div className="h-screen flex items-center justify-center">
        <Loader />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 text-red-700 rounded-lg">
        <p>{error}</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="h-screen flex items-center justify-center">
        <Loader />
      </div>
    );
  }

  if (!classData) {
    return (
      <div className="p-4 text-gray-500">
        Class not found
      </div>
    );
  }

  return (
    <div className="h-screen overflow-hidden flex flex-col" style={{ background: theme.background, color: theme.text }}>
      <header className="flex-shrink-0 border-b border-theme">
        <div className="h-full px-4 py-4 sm:px-6 lg:px-8">
          <nav className="flex items-center text-sm select-text">
            <button
              onClick={() => navigate('/classes')}
              className="text-accent hover:text-theme hover:underline flex items-center gap-2"
            >
              <ChevronLeft size={16} />
              Back to Classes
            </button>
          </nav>

          <div className="flex items-center justify-between mt-4">
            <div className="select-text">
              <h1 className="text-2xl font-bold text-theme">
                {classData.title}
              </h1>
              <p className="text-sm text-accent">
                {classData.professor} • {classData.location} • {classData.credits} credits
              </p>
            </div>
            <button
              onClick={handleAddToAccount}
              disabled={isAdding || selectedLectures.size === 0}
              className="px-4 py-2 rounded-lg flex items-center gap-2 bg-primary hover:bg-primary-hover text-theme transform transition-all duration-200 hover:scale-[1.02]"
              style={{ 
                opacity: (isAdding || selectedLectures.size === 0) ? 0.5 : 1,
                cursor: (isAdding || selectedLectures.size === 0) ? 'not-allowed' : 'pointer'
              }}
            >
              <CheckSquare size={18} />
              {isAdding ? 'Adding...' : 'Add to My Account'}
            </button>
          </div>
        </div>
      </header>

      <main className="flex-1 overflow-auto p-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-3 gap-6">
            <div className="col-span-2">
              <div className="p-6 rounded-lg bg-theme border border-theme theme-transition">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-medium text-theme">Lectures</h2>
                  <button
                    onClick={handleSelectAllToggle}
                    className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm"
                    style={{ 
                      background: theme.secondary,
                      color: theme.text
                    }}
                  >
                    {selectAll ? <CheckSquare size={16} /> : <Square size={16} />}
                    {selectAll ? 'Deselect All' : 'Select All'}
                  </button>
                </div>
                
                <div className="space-y-3">
                  {classData.lectures.map(lecture => (
                    <div
                      key={lecture.id}
                      className="flex items-center p-4 rounded-lg"
                      style={{ 
                        background: theme.secondary,
                        border: `1px solid ${theme.border}`
                      }}
                    >
                      <button
                        onClick={() => handleLectureToggle(lecture.id)}
                        className="flex items-center gap-3 flex-1"
                      >
                        <div 
                          className="w-5 h-5 rounded flex items-center justify-center"
                          style={{ 
                            background: selectedLectures.has(lecture.id) ? theme.primary : 'transparent',
                            border: `2px solid ${selectedLectures.has(lecture.id) ? theme.primary : theme.border}`
                          }}
                        >
                          {selectedLectures.has(lecture.id) && <Check size={14} />}
                        </div>
                        <div className="flex-1">
                          <h3 className="font-medium text-theme">{lecture.title}</h3>
                          <p className="text-sm text-accent">
                            {new Date(lecture.date).toLocaleDateString('en-US', {
                              weekday: 'long',
                              month: 'long',
                              day: 'numeric',
                              year: 'numeric'
                            })}
                          </p>
                        </div>
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="space-y-6">
              <div className="p-6 rounded-lg bg-theme border border-theme theme-transition select-text">
                <div className="flex items-center gap-2 mb-4">
                  <Book size={20} className="text-accent" />
                  <h3 className="text-lg font-medium text-theme">Class Information</h3>
                </div>
                <div className="space-y-2">
                  <p className="text-theme font-medium">{classData.title}</p>
                  <p className="text-accent">Professor: {classData.professor}</p>
                  <p className="text-accent">Location: {classData.location}</p>
                  <p className="text-accent">Credits: {classData.credits}</p>
                  {classData.description && (
                    <p className="text-accent mt-4">{classData.description}</p>
                  )}
                </div>
              </div>

              <div className="p-6 rounded-lg bg-theme border border-theme theme-transition">
                <div className="flex items-center gap-2 mb-4">
                  <Info size={20} className="text-accent" />
                  <h3 className="text-lg font-medium text-theme">Import Summary</h3>
                </div>
                <div className="space-y-2">
                  <p className="text-accent">
                    Selected {selectedLectures.size} of {classData.lectures.length} lectures
                  </p>
                  {selectedLectures.size === 0 && (
                    <p className="text-sm text-error mt-2">
                      Please select at least one lecture to import
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default SharedClassView; 